import { Alert, Heading, Option, RadioInput } from '@mm/components';
import { useTwilioCRUDActions } from '@mm/state/actions/TwilioCRUDActions';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import {
  CampaignAudienceAttribute,
  CampaignSchedulerAttribute,
} from '@mm/state/model/CampaignsModel';
import modalActions from '@mm/state/modules/modalsState/actions';
import { StoreState } from '@mm/state/store-interfaces';
import { PlusCircleIcon } from '@mm/icons';
import { colors } from '@mm/utils';
import classnames from 'classnames';
import { get, sortBy } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { AcceptableTimeBuilder } from '../../../../../../common/components/AcceptableTimeBuilder/AcceptableTimeBuilder';
import { MMdndDroppable } from '../../../../../../common/components/MMdndDroppable/MMdndDroppable';
import { TooltipGuard } from '../../../../../../modules/permissions/RequiresPermissions';
import {
  AudiencesSelector,
  mapAudienceAttribute,
} from '../../../components/AudiencesSelector/AudiencesSelector';
import DripCampaignTimelineItem from '../../../components/DripCampaignTimelineItem/DripCampaignTimelineItem';
import { PROMOTIONAL_PURPOSE } from '../../../constants/purposes';
import { CampaignErrors } from '../../ChatBlasterDetailsPanel/ChatBlasterDetailsPanel';
import { DripCampaignProps } from '../DripCampaignDetailsPanel';
import './DripCampaignSteps.pcss';
import Checkbox from '../../../../../../common/components/Checkbox';

export function mapOptionToAudienceAttribute({ id, label, ...rest }: Option) {
  return { audienceId: id, name: label, ...rest } as CampaignAudienceAttribute;
}

const getFirstUnreachable = (
  schedulers: CampaignSchedulerAttribute[],
): number => {
  const sorted = sortBy(schedulers, 'sortOrder');
  const DAY = 1440;
  let totalMinutes = 0;

  for (let i = 0; i < sorted.length; i += 1) {
    const scheduler = sorted[i];

    totalMinutes += scheduler.intervalInMin;
    if (totalMinutes > DAY) return scheduler.sortOrder;
  }

  return Number.MAX_SAFE_INTEGER;
};

const getStartBotButtonText = (
  filteredSchedulersAttributes: CampaignSchedulerAttribute[],
) => {
  if (filteredSchedulersAttributes.length) {
    return 'Add another Drip Campaign Dialogue';
  }

  return 'Add Drip Campaign Dialogue';
};

export const DripCampaignSteps = (
  props: DripCampaignProps & {
    errors: CampaignErrors;
    onSaveCampaign: () => void;
  },
) => {
  const {
    errors,
    savedCampaign,
    selectedPurposeId,
    savedCampaignPaused,
    savedCampaignActive,
    includedAudiences,
    setIncludedAudiences,
    excludedAudiences,
    setExcludedAudiences,
    acceptableTime,
    setAcceptableTime,
    setSelectedSchedulerId,
    setQueueModalOpen,
    savedCampaignReadOnly,
    onDragEnd,
    deleteScheduler,
    onChangeScheduler,
    onAddTimelineItem,
    schedulersAttributes,
    smsSupported,
    platforms,
    chatbot,
    activeFacebookPage,
    setPlatforms,
    openPhonePreview,
    currentDialog,
    onSaveCampaign,
    allowRequeued,
    setAllowRequeueing,
  } = props;
  const dispatch: Dispatch<StoreState> = useDispatch();
  const twilioCRUDActions = useTwilioCRUDActions();
  const purposeIsPromotional = selectedPurposeId === PROMOTIONAL_PURPOSE;
  const savedCampaignCanceled = !!(
    savedCampaign && savedCampaign.status === 'canceled'
  );
  const stepsContainerRef = useRef<HTMLDivElement | null>(null);

  const setPlatform = (platform: string) => {
    const off = Object.fromEntries(
      Object.entries(platforms).map(([k]) => [k, false]),
    );

    return setPlatforms({
      ...off,
      [platform]: true,
    } as {
      facebook: boolean;
      sms: boolean;
      instagram: boolean;
    });
  };

  const addTimeLineItemAndScroll = async () => {
    await onAddTimelineItem();
    if (stepsContainerRef.current) {
      stepsContainerRef.current?.scrollIntoView({
        block: 'end',
        behavior: 'smooth',
      });
    }
  };

  let orderNumber = 0;
  const getOrderNumber = () => {
    orderNumber += 1;

    return orderNumber.toString();
  };

  const filteredSchedulersAttributes = schedulersAttributes
    ? schedulersAttributes.filter(s => !s.destroy)
    : [];

  useEffect(() => {
    if (currentDialog === -1 && filteredSchedulersAttributes.length) {
      openPhonePreview(filteredSchedulersAttributes[0].destinationPageId);
    }
  }, [filteredSchedulersAttributes]);
  const savedCampaignSchedulerAttributesCount: number | undefined = get(
    savedCampaign,
    ['schedulersAttributes', 'length'],
  );

  const openQueueModal = (schedulerId: number) => {
    setSelectedSchedulerId(schedulerId);
    setQueueModalOpen(true);
  };

  const firstUnreachableScheduler = getFirstUnreachable(
    filteredSchedulersAttributes,
  );

  const classList = classnames('drip-campaign-steps', {
    'drip-campaign-steps--canceled': savedCampaignCanceled,
    'drip-campaign-steps--active': savedCampaignActive,
  });

  const onInstagramToggle = () => {
    if (!activeFacebookPage?.instagramActivated) {
      dispatch(
        modalActions.openGeneralConfirmModal({
          headerText: 'Activate Instagram',
          messageText:
            'You need to active Instagram to send Drip Campaign through Instagram.',
          okButtonText: 'Connect',
          okButtonStyling: {
            blue: true,
          },
          cancelButtonFirst: true,
          onCloseHandler: ok => {
            if (ok) {
              dispatch(modalActions.openInstagramConnectModal());
            }
          },
        }),
      );
      return;
    }

    setPlatform('instagram');
  };

  useEffect(() => {
    if (smsSupported && activeFacebookPage) {
      twilioCRUDActions.getAll(activeFacebookPage.id, smsSupported);
    }
  }, []);

  const onDeleteScheduler = (scheduler: CampaignSchedulerAttribute) => {
    openPhonePreview(0);
    deleteScheduler(scheduler);
  };

  return (
    <div className={classList} ref={stepsContainerRef}>
      <div className="drip-campaign-steps__step">
        <div className="drip-campaign-steps__step-title">
          <span className="drip-campaign-steps__step-title-number">
            {getOrderNumber()}
          </span>
          <Heading size={4} text="Specify the platform to send Drip Campaign" />
        </div>
        <div className="drip-campaign-steps__field">
          {errors.platforms && (
            <Alert
              className="drip-campaign-detail__error"
              type="error"
              showCloseIcon={false}
            >
              {errors.platforms}
            </Alert>
          )}

          <TooltipGuard
            position="top"
            classNameContent="drip-campaign-steps__guard"
          >
            {isAllowed => (
              <div
                className="drip-campaign-steps__check-area"
                onClick={
                  !isAllowed || savedCampaignReadOnly
                    ? undefined
                    : () => setPlatform('facebook')
                }
              >
                <RadioInput
                  name="platform"
                  value="fb"
                  disabled={!isAllowed || savedCampaignReadOnly}
                  checked={platforms.facebook && isAllowed}
                  readOnly
                />
                <span className="drip-campaign-steps__check-area-text">
                  Facebook Messenger
                </span>
              </div>
            )}
          </TooltipGuard>

          <div
            className="drip-campaign-steps__check-area"
            onClick={savedCampaignReadOnly ? undefined : onInstagramToggle}
          >
            <RadioInput
              name="platform"
              value="instagram"
              checked={platforms.instagram}
              disabled={savedCampaignReadOnly}
            />
            <span className="drip-campaign-steps__check-area-text">
              Instagram
            </span>
          </div>
        </div>
      </div>

      <div className="drip-campaign-steps__step">
        <div className="drip-campaign-steps__step-title">
          <span className="drip-campaign-steps__step-title-number">
            {getOrderNumber()}
          </span>
          <Heading
            size={4}
            text="Choose the audience(s) this Drip Campaign should be sent to"
          />
        </div>
        <div className="drip-campaign-steps__field">
          {errors.audience && (
            <Alert
              className="drip-campaign-detail__error"
              type="error"
              showCloseIcon={false}
            >
              {errors.audience}
            </Alert>
          )}

          <div className="drip-campaign-detail__audience-selectors">
            <AudiencesSelector
              disabled={savedCampaignPaused || savedCampaignActive}
              className="drip-campaign-detail__audience-selector"
              label="Included:"
              selected={includedAudiences.map(mapAudienceAttribute)}
              onChange={v =>
                setIncludedAudiences(v.map(mapOptionToAudienceAttribute))
              }
              withThreeDotMenu
            />

            <AudiencesSelector
              disabled={savedCampaignPaused || savedCampaignActive}
              className="drip-campaign-detail__audience-selector"
              label="Excluded:"
              selected={excludedAudiences.map(mapAudienceAttribute)}
              onChange={v =>
                setExcludedAudiences(v.map(mapOptionToAudienceAttribute))
              }
            />
          </div>
        </div>
      </div>

      <div className="drip-campaign-steps__step">
        <div className="drip-campaign-steps__step-title">
          <span className="drip-campaign-steps__step-title-number">
            {getOrderNumber()}
          </span>
          <Heading
            size={4}
            text="Choose a schedule during which your drip campaign messages will be sent (each contact's timezone)"
          />
        </div>
        <div className="drip-campaign-steps__field">
          <AcceptableTimeBuilder
            acceptableTime={acceptableTime}
            onChangeAcceptableTime={setAcceptableTime}
            allowNextDayStopTime={false}
            isPromotional={purposeIsPromotional}
            isFacebookPlatform={platforms.facebook}
          />
        </div>
        <Checkbox
          className="drip-campaign-steps__checkbox"
          labelClassName="drip-campaign-steps__checkbox-label"
          isChecked={allowRequeued || false}
          label="Allow sending to same contact multiple times"
          disabled={
            savedCampaign?.status === 'canceled' || savedCampaign?.active
          }
          onToggle={() => setAllowRequeueing(!allowRequeued)}
        />
      </div>

      <div className="drip-campaign-steps__step">
        <div className="drip-campaign-steps__step-title">
          <span className="drip-campaign-steps__step-title-number">
            {getOrderNumber()}
          </span>
          <Heading
            size={4}
            text="Choose or create your Drip Campaign Dialogues"
          />
        </div>
        <div className="drip-campaign-steps__field">
          <MMdndDroppable
            droppableId="drip-campaign__droppable"
            type="drip-campaign-droppable"
            onDragEnd={onDragEnd}
          >
            {provided => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {filteredSchedulersAttributes.map((scheduler, index) => (
                  <Draggable
                    key={scheduler.id}
                    draggableId={`${index}`}
                    index={index}
                    isDragDisabled={savedCampaignReadOnly}
                  >
                    {provided => (
                      <DripCampaignTimelineItem
                        index={index}
                        savedCampaignCanceled={savedCampaignCanceled}
                        scheduler={scheduler}
                        campaign={savedCampaign}
                        onChangeScheduler={onChangeScheduler}
                        deleteScheduler={onDeleteScheduler}
                        readonly={
                          savedCampaignReadOnly &&
                          (!savedCampaignSchedulerAttributesCount ||
                            index < savedCampaignSchedulerAttributesCount)
                        }
                        allowChangeDialogue={savedCampaignPaused}
                        showSchedulerStats={
                          savedCampaignReadOnly || savedCampaignPaused
                        }
                        draggableProvided={provided}
                        openQueueModal={openQueueModal}
                        reachable={
                          scheduler.sortOrder < firstUnreachableScheduler
                        }
                        promotional={purposeIsPromotional}
                        platforms={platforms}
                        onSaveCampaign={onSaveCampaign}
                      />
                    )}
                  </Draggable>
                ))}
              </div>
            )}
          </MMdndDroppable>

          {(!savedCampaignReadOnly || savedCampaignPaused) && (
            <PrimaryButton
              onClick={addTimeLineItemAndScroll}
              className="drip-campaign-steps__add-drip-btn"
              leftIcon={<PlusCircleIcon color={colors.blue} />}
            >
              {getStartBotButtonText(filteredSchedulersAttributes)}
            </PrimaryButton>
          )}
          <div className="drip-campaign-steps__extra-space" />
        </div>
      </div>
    </div>
  );
};
